import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppRouter } from '@hooks/navigation';
import { useContactRouter } from '@hooks/useContactRouter';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { AppRouteEnum, CallDirectionEnum, PersistedStateKeyEnum, QueryKeyEnum } from '@lib/enums';
import { LocaleEnum } from '@lib/enums/LocaleEnum';
import { useJotaiAtom, useJotaiValue } from '@states/jotai/hooks';
import { LocaleAtom } from '@states/localeAtom';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom, INITIAL_CURRENT_CALL_STATE } from '@states/softphone/currentCallAtom';
export type TUseCallRedirectReturn = {
  performCallRedirect: () => void;
};
export function useCallRedirect() {
  const $ = _c(27);
  const queryClient = useQueryClient();
  const router = useAppRouter();
  const {
    isCustomerService,
    isRetention
  } = useCurrentUser();
  const [, setLocale] = useJotaiAtom(LocaleAtom);
  const [t0, setCurrentCallState] = useJotaiAtom(currentCallAtom);
  const {
    lastTouchedQuoteId
  } = t0;
  const {
    contactId,
    contactMasterId,
    callDirection,
    transferQuoteId,
    transferContractId,
    transferConsumerId,
    isTransfer,
    isDialerReturn,
    isInternetResponder,
    isTvResponder,
    isSpanishSkillActive
  } = useJotaiValue(contactSelector);
  const {
    goToSearchPageForCurrentContact,
    goToContactPreview,
    goToQuoteFlowEntry,
    goToConsumerProfile,
    goToMoxyCustomerNumberSearch,
    goToContractDetails
  } = useContactRouter();
  let t1;
  if ($[0] !== callDirection || $[1] !== contactId || $[2] !== contactMasterId || $[3] !== goToConsumerProfile || $[4] !== goToContactPreview || $[5] !== goToContractDetails || $[6] !== goToMoxyCustomerNumberSearch || $[7] !== goToQuoteFlowEntry || $[8] !== goToSearchPageForCurrentContact || $[9] !== isCustomerService || $[10] !== isDialerReturn || $[11] !== isInternetResponder || $[12] !== isRetention || $[13] !== isSpanishSkillActive || $[14] !== isTransfer || $[15] !== isTvResponder || $[16] !== lastTouchedQuoteId || $[17] !== queryClient || $[18] !== router || $[19] !== setCurrentCallState || $[20] !== setLocale || $[21] !== transferConsumerId || $[22] !== transferContractId || $[23] !== transferQuoteId) {
    t1 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Consumer]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Vehicle]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.VehicleQuote]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.CallDetails]
      });
      localStorage.removeItem(PersistedStateKeyEnum.SearchType);
      localStorage.removeItem(PersistedStateKeyEnum.ManualSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchType);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.SearchMoxyCustomerNumberFormState);
      setCurrentCallState(() => ({
        ...INITIAL_CURRENT_CALL_STATE,
        lastRoutedContactId: contactId,
        lastRoutedContactMasterId: contactMasterId,
        lastTouchedQuoteId
      }));
      setLocale({
        locale: isSpanishSkillActive === true ? LocaleEnum.ES_MX : LocaleEnum.EN_US
      });
      if (transferContractId != null) {
        goToContractDetails(transferContractId);
        return;
      }
      if (transferQuoteId != null) {
        goToQuoteFlowEntry(transferQuoteId);
        return;
      }
      if (transferConsumerId != null) {
        goToConsumerProfile(transferConsumerId);
        return;
      }
      if (isCustomerService) {
        router.build(AppRouteEnum.Dashboard).go();
        return;
      }
      if (isRetention) {
        goToSearchPageForCurrentContact();
        return;
      }
      if (isTransfer === true) {
        goToMoxyCustomerNumberSearch();
        return;
      }
      if (isDialerReturn === true) {
        router.build(AppRouteEnum.ContactSearchAuto).go();
        return;
      }
      if (isInternetResponder === true || isTvResponder === true) {
        router.build(AppRouteEnum.ContactSearchMail).go();
        return;
      }
      if (callDirection === CallDirectionEnum.Outbound) {
        goToContactPreview();
        return;
      }
      goToSearchPageForCurrentContact();
    };
    $[0] = callDirection;
    $[1] = contactId;
    $[2] = contactMasterId;
    $[3] = goToConsumerProfile;
    $[4] = goToContactPreview;
    $[5] = goToContractDetails;
    $[6] = goToMoxyCustomerNumberSearch;
    $[7] = goToQuoteFlowEntry;
    $[8] = goToSearchPageForCurrentContact;
    $[9] = isCustomerService;
    $[10] = isDialerReturn;
    $[11] = isInternetResponder;
    $[12] = isRetention;
    $[13] = isSpanishSkillActive;
    $[14] = isTransfer;
    $[15] = isTvResponder;
    $[16] = lastTouchedQuoteId;
    $[17] = queryClient;
    $[18] = router;
    $[19] = setCurrentCallState;
    $[20] = setLocale;
    $[21] = transferConsumerId;
    $[22] = transferContractId;
    $[23] = transferQuoteId;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  const performCallRedirect = t1;
  let t2;
  if ($[25] !== performCallRedirect) {
    t2 = {
      performCallRedirect
    };
    $[25] = performCallRedirect;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  return t2;
}